"use client";
import { motion } from "framer-motion";

interface AnimatedHamburgerProps {
  isOpen: boolean;
  onClick: () => void;
  color?: string;
  openColor?: string;
  size?: number;
  lineHeight?: number;
}

const AnimatedHamburger = ({
  isOpen,
  onClick,
  color = "#fff",
  openColor = "#fff",
  size = 24,
  lineHeight = 2,
}: AnimatedHamburgerProps) => {
  const fullWidth = size * 0.95;
  const halfWidth = fullWidth * 0.6;
  const gap = size * 0.22;
  const currentColor = isOpen ? openColor : color;
  const center = size / 2;
  const edgeOffset = (size - fullWidth) / 2;

  const smoothTransition = {
    duration: 0.4,
    ease: [0.25, 1, 0.5, 1] as const,
  };

  return (
    <button
      onClick={onClick}
      className="relative flex items-center justify-center cursor-pointer focus:outline-none"
      style={{ width: size, height: size, zIndex: 210 }}
      aria-label="Toggle menu"
    >
      {/* ====== Top Line  ====== */}
      <motion.span
        style={{
          height: lineHeight,
          borderRadius: lineHeight,
          backgroundColor: currentColor,
          position: "absolute",
          right: (size - fullWidth) / 2,
          transformOrigin: "right center",
        }}
        animate={
          isOpen
            ? {
                width: fullWidth,
                rotate: -45,
                top: "14%",
                left: "48%",
                right: "auto",
                x: "-60%",
                y: "-40%",
              }
            : {
                width: halfWidth,
                rotate: 0,
                top: center - gap,
                left: "auto",
                right: edgeOffset,
                x: "0%",
                y: "-50%",
              }
        }
        transition={smoothTransition}
      />

      {/* ====== Middle Line ====== */}
      <motion.span
        style={{
          height: lineHeight,
          borderRadius: lineHeight,
          backgroundColor: currentColor,
          position: "absolute",
          left: "50%",
          x: "-50%",
          top: "50%",
          y: "-50%",
        }}
        animate={
          isOpen ? { width: 0, opacity: 0 } : { width: fullWidth, opacity: 1 }
        }
        transition={{ duration: 0.25, ease: "easeInOut" }}
      />

      {/* ====== Bottom Line ====== */}
      <motion.span
        style={{
          height: lineHeight,
          borderRadius: lineHeight,
          backgroundColor: currentColor,
          position: "absolute",
          left: (size - fullWidth) / 2,
          transformOrigin: "left center",
        }}
        animate={
          isOpen
            ? {
                width: fullWidth,
                rotate: 45,
                top: "12%",
                left: "38%",
                x: "-20%",
                y: "-40%",
              }
            : {
                width: halfWidth,
                rotate: 0,
                top: center + gap,
                left: edgeOffset,
                x: 0,
                y: "-50%",
              }
        }
        transition={smoothTransition}
      />
    </button>
  );
};

export default AnimatedHamburger;
